import { Input } from "@aix/ui"
import { useNavigate, useSearch } from "@tanstack/react-router"
import React, { useEffect, useState } from "react"
import toast from "react-hot-toast"

import { trpc } from "../../lib/trpc"
import DebugTab from "./components/DebugTab"
import { NewProjectModal } from "./components/NewProjectModal"
import { NewTaskModal } from "./components/NewTaskModal"
import { ProjectsList } from "./components/ProjectsList"
import { SearchResults } from "./components/SearchResults"
import { TaskDetails } from "./components/TaskDetails"
import { TasksList } from "./components/TasksList"

type TabType = "projects" | "tasks" | "search" | "debug"

const MultiLanguageTestAppScreen: React.FC = () => {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as {
    tab?: string
    projectId?: string
    taskId?: string
  }

  // State management
  const [activeTab, setActiveTab] = useState<TabType>((search.tab as TabType) || "projects")
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(search.projectId || null)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(search.taskId || null)
  const [searchQuery, setSearchQuery] = useState("")
  const [showNewProjectModal, setShowNewProjectModal] = useState(false)
  const [showNewTaskModal, setShowNewTaskModal] = useState(false)

  // Sync URL with state
  useEffect(() => {
    navigate({
      to: "/multi-language-test",
      search: {
        // @ts-ignore
        tab: activeTab,
        // @ts-ignore
        projectId: selectedProjectId || undefined,
        // @ts-ignore
        taskId: selectedTaskId || undefined,
      },
    })
  }, [activeTab, selectedProjectId, selectedTaskId, navigate, search])

  // API queries
  const {
    data: projectsData,
    isLoading: projectsLoading,
    refetch: refetchProjects,
  } = trpc.multiLanguageTest.listProjects.useQuery({})

  const {
    data: tasksData,
    isLoading: tasksLoading,
    refetch: refetchTasks,
  } = trpc.multiLanguageTest.listTasks.useQuery({ projectId: selectedProjectId }, { enabled: !!selectedProjectId })

  const { data: taskDetails, isLoading: taskDetailsLoading } = trpc.multiLanguageTest.getTask.useQuery(
    { taskId: selectedTaskId },
    { enabled: !!selectedTaskId },
  )

  const { data: searchResults, isLoading: searchLoading } = trpc.multiLanguageTest.searchTasks.useQuery(
    { query: searchQuery },
    { enabled: searchQuery.length > 2 },
  )

  const { data: commentsData, refetch: refetchComments } = trpc.multiLanguageTest.listComments.useQuery(
    { taskId: selectedTaskId },
    { enabled: !!selectedTaskId },
  )

  const {
    data: debugInfo,
    refetch: refetchDebugInfo,
    isLoading: isRefetchingDebugInfo,
  } = trpc.multiLanguageTest.getDebugInfo.useQuery()

  // Mutations
  const createProjectMutation = trpc.multiLanguageTest.createProject.useMutation({
    onSuccess: (data) => {
      toast.success("Project created successfully!")
      void refetchProjects()
      setShowNewProjectModal(false)
      setSelectedProjectId(data.id)
      setActiveTab("tasks")
    },
    onError: (error) => {
      toast.error(`Failed to create project: ${error.message}`)
    },
  })

  const createTaskMutation = trpc.multiLanguageTest.createTask.useMutation({
    onSuccess: () => {
      toast.success("Task created successfully!")
      void refetchTasks()
      setShowNewTaskModal(false)
    },
    onError: (error) => {
      toast.error(`Failed to create task: ${error.message}`)
    },
  })

  const updateTaskMutation = trpc.multiLanguageTest.updateTask.useMutation({
    onSuccess: () => {
      toast.success("Task updated successfully!")
      void refetchTasks()
    },
    onError: (error) => {
      toast.error(`Failed to update task: ${error.message}`)
    },
  })

  const createCommentMutation = trpc.multiLanguageTest.createComment.useMutation({
    onSuccess: () => {
      toast.success("Comment added successfully!")
      void refetchComments()
    },
    onError: (error) => {
      toast.error(`Failed to add comment: ${error.message}`)
    },
  })

  // Event handlers
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab)
    if (tab !== "tasks") {
      setSelectedTaskId(null)
    }
    if (tab !== "tasks" && tab !== "projects") {
      setSelectedProjectId(null)
    }
  }

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId)
    setActiveTab("tasks")
    setSelectedTaskId(null)
  }

  const handleTaskSelect = (taskId: string) => {
    setSelectedTaskId(taskId)
  }

  const handleCreateProject = (data: { name: string; description?: string }) => {
    createProjectMutation.mutate(data)
  }

  const handleCreateTask = (data: {
    title: string
    body?: string
    priority?: number
    assigneeId?: string
    dueDate?: string
  }) => {
    if (!selectedProjectId) {
      toast.error("Please select a project first")
      return
    }
    createTaskMutation.mutate({ ...data, projectId: selectedProjectId })
  }

  const handleUpdateTask = (taskId: string, data: any) => {
    updateTaskMutation.mutate({ taskId, ...data })
  }

  const handleAddComment = (body: string) => {
    if (!selectedTaskId) return
    createCommentMutation.mutate({ taskId: selectedTaskId, body })
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    if (query.trim().length > 2) {
      setActiveTab("search")
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">🚀 Multi-Language Test App</h1>
          <p className="text-muted-foreground">
            A comprehensive task management system with projects, tasks, comments, and search functionality.
          </p>
        </div>

        <div className="flex gap-6">
          {/* Main Content */}
          <div className="flex-1">
            {/* Tabs */}
            <div className="flex border-b border-border mb-6">
              <button
                onClick={() => handleTabChange("projects")}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === "projects"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                📋 Projects
              </button>
              <button
                onClick={() => handleTabChange("tasks")}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === "tasks"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                ✅ Tasks
              </button>
              <button
                onClick={() => handleTabChange("search")}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === "search"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                🔍 Search
              </button>
              <button
                onClick={() => handleTabChange("debug")}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === "debug"
                    ? "border-b-2 border-primary text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                🐛 Debug
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="border border-border rounded-md px-3 py-2 w-full"
                placeholder="Search tasks..."
              />
            </div>

            {/* Content based on active tab */}
            {activeTab === "projects" && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Projects</h2>
                  <button
                    onClick={() => setShowNewProjectModal(true)}
                    className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90"
                  >
                    New Project
                  </button>
                </div>
                <ProjectsList
                  projects={
                    projectsData?.items.map((item) => ({
                      ...item,
                      createdAt: item.createdAt instanceof Date ? item.createdAt.toISOString() : item.createdAt,
                    })) || []
                  }
                  isLoading={projectsLoading}
                  onProjectSelect={handleProjectSelect}
                  selectedProjectId={selectedProjectId}
                />
              </>
            )}

            {activeTab === "tasks" && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    Tasks{" "}
                    {selectedProjectId &&
                      projectsData?.items.find((p) => p.id === selectedProjectId)?.name &&
                      `- ${projectsData.items.find((p) => p.id === selectedProjectId)?.name}`}
                  </h2>
                  <button
                    onClick={() => setShowNewTaskModal(true)}
                    disabled={!selectedProjectId}
                    className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    New Task
                  </button>
                </div>
                {selectedProjectId ? (
                  <TasksList
                    tasks={
                      tasksData?.items.map((item) => ({
                        ...item,
                        createdAt: item.createdAt instanceof Date ? item.createdAt.toISOString() : item.createdAt,
                        updatedAt: item.updatedAt instanceof Date ? item.updatedAt.toISOString() : item.updatedAt,
                      })) || []
                    }
                    isLoading={tasksLoading}
                    onTaskSelect={handleTaskSelect}
                    onUpdateTask={handleUpdateTask}
                    selectedTaskId={selectedTaskId}
                    updateTaskLoading={updateTaskMutation.isLoading}
                  />
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📋</div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Select a project</h3>
                    <p className="text-muted-foreground">Choose a project from the Projects tab to view its tasks</p>
                  </div>
                )}
              </>
            )}

            {activeTab === "search" && (
              <>
                <h2 className="text-xl font-semibold mb-4">Search Results</h2>
                <SearchResults
                  results={
                    searchResults?.items.map((item) => ({
                      ...item,
                      createdAt: item.createdAt instanceof Date ? item.createdAt.toISOString() : item.createdAt,
                      updatedAt: item.updatedAt instanceof Date ? item.updatedAt.toISOString() : item.updatedAt,
                    })) || []
                  }
                  isLoading={searchLoading}
                  searchQuery={searchQuery}
                  onTaskSelect={handleTaskSelect}
                />
              </>
            )}

            {activeTab === "debug" && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Debug Information</h2>
                  <button
                    onClick={() => refetchDebugInfo()}
                    disabled={isRefetchingDebugInfo}
                    className="bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/90 disabled:opacity-50"
                  >
                    {isRefetchingDebugInfo ? "Refreshing..." : "Refresh Data"}
                  </button>
                </div>
                <DebugTab
                  debugInfo={debugInfo}
                  projectsData={projectsData}
                  tasksData={tasksData}
                  searchResults={searchResults}
                />
              </>
            )}
          </div>

          {/* Task Details Sidebar */}
          {selectedTaskId && (
            <div className="w-1/3 flex-shrink-0">
              <TaskDetails
                taskId={selectedTaskId}
                isLoading={taskDetailsLoading}
                comments={
                  commentsData?.items.map((item) => ({
                    ...item,
                    createdAt: item.createdAt instanceof Date ? item.createdAt.toISOString() : item.createdAt,
                    updatedAt: item.updatedAt instanceof Date ? item.updatedAt.toISOString() : item.updatedAt,
                  })) || []
                }
                onClose={() => setSelectedTaskId(null)}
                onUpdateTask={handleUpdateTask}
                onAddComment={handleAddComment}
                updateTaskLoading={updateTaskMutation.isLoading}
                addCommentLoading={createCommentMutation.isLoading}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <NewProjectModal
        isOpen={showNewProjectModal}
        onClose={() => setShowNewProjectModal(false)}
        onSubmit={handleCreateProject}
        isLoading={createProjectMutation.isLoading}
      />

      <NewTaskModal
        isOpen={showNewTaskModal}
        onClose={() => setShowNewTaskModal(false)}
        onSubmit={handleCreateTask}
        isLoading={createTaskMutation.isLoading}
        projectId={selectedProjectId}
      />
    </div>
  )
}

export default MultiLanguageTestAppScreen
